import { FC, useEffect, useState, useRef } from 'react';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import StyledGridContainer, {
  CenteredCellBox,
  LeftAlignedCellBox,
} from 'src/components/StyledComponents/StyledGridContainer';
import Status from 'src/components/Status';
import CustomNoRowsOverlay from 'src/components/StyledComponents/CustomEmptyTableRow';
import { GRN } from 'src/models/Management.interface';
import LoadingTable from 'src/components/StyledComponents/CustomLoadingTable';
import StyledDataGrid from 'src/components/StyledComponents/StyledDataGrid';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import PaginationControl from 'src/components/StyledComponents/StyledPagination';
import { useGetGRN } from 'src/services/grn.service';
import GRNActionMenuButton from 'src/components/ActionButtons/GRNActionMenu';
import { loadData } from 'src/utils/grid.utils';
import {
  errorMessages,
  pagination,
  DATE_FORMATS,
} from 'src/utils/properties.utils';
import { formatCurrency } from 'src/utils/format.utils';
import { getGRNStatusText } from 'src/utils/status.utils';

const GRNGrid: FC<{
  state: boolean;
  searchTerm: string;
  filters: {
    vendorId: string;
    status: string;
    startDate: Date | null;
    endDate: Date | null;
  };
}> = ({ state, searchTerm, filters }) => {
  const { showSnackbar } = StyledSnackbar();
  const {
    loading,
    grns,
    totalItems,
    currentPage,
    limit,
    setLimit,
    fetchGRNs,
    deleteGRN,
  } = useGetGRN();
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [page, setPage] = useState(pagination.DEFAULT_PAGE);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'createdAt', sort: 'desc' },
  ]);
  const prevStateRef = useRef(state);

  useEffect(() => {
    if (prevStateRef.current !== state) {
      prevStateRef.current = state;
      if (page !== pagination.DEFAULT_PAGE) {
        setPage(pagination.DEFAULT_PAGE);
        return;
      }
    }

    loadData(
      () =>
        fetchGRNs(
          state ? 1 : page,
          limit,
          searchTerm,
          sortBy,
          order,
          filters.status,
          filters.vendorId,
          filters.startDate,
          filters.endDate
        ),
      showSnackbar
    );
  }, [sortBy, order, state, page, limit, searchTerm, filters, refreshTrigger]);

  useEffect(() => {
    setPage(pagination.DEFAULT_PAGE);
  }, [searchTerm, filters]);

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    const order = (newSortModel[0]?.sort || 'desc').toUpperCase();
    let sortBy = newSortModel[0]?.field || 'createdAt';
    if (sortBy === 'vendor') {
      sortBy = 'vendor.organizationName';
    }
    setSortBy(sortBy);
    setOrder(order as 'ASC' | 'DESC');
    setPage(pagination.DEFAULT_PAGE);
  };
  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: '#',
      flex: 0.15,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      flex: 0.4,
    },
    {
      field: 'vendor',
      headerName: 'Vendor',
      flex: 0.8,
    },
    {
      field: 'invoiceNumber',
      headerName: 'Invoice Number',
      flex: 0.5,
    },
    {
      field: 'status',
      headerName: 'Status',
      headerAlign: 'center',
      flex: 0.5,
      renderCell: (params) => (
        <CenteredCellBox>
          <Status status={params.value} />
        </CenteredCellBox>
      ),
    },
    {
      field: 'invoiceAmount',
      headerName: 'Invoice Amount',
      flex: 0.5,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'totalPaidAmount',
      headerName: 'Total Paid Amount',
      flex: 0.6,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.3,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <GRNActionMenuButton
          id={params.row.id}
          status={params.row.rawStatus}
          resetPageAndRefresh={() => {
            if (page === 1) {
              setRefreshTrigger((prev) => prev + 1);
            } else {
              setPage(1);
            }
          }}
          triggerRefresh={() => {
            setRefreshTrigger((prev) => prev + 1);
          }}
          deleteGRN={deleteGRN}
        />
      ),
    },
  ];

  const transformData = (grns: GRN[]) => {
    if (!grns || grns.length === 0) {
      return [];
    }

    return grns.map((grn, index) => ({
      id: grn.id,
      number: (currentPage - 1) * limit + index + 1,
      createdAt: new Date(grn.createdAt).toLocaleDateString(
        DATE_FORMATS.LOCALE
      ),
      vendor: grn.vendorName,
      invoiceNumber: grn.invoiceNumber,
      status: getGRNStatusText(grn.status),
      invoiceAmount: grn.invoiceAmount,
      totalPaidAmount: grn.totalPaidAmount || 0,
      rawStatus: grn.status,
    }));
  };

  const rows = transformData(grns);

  return loading ? (
    <LoadingTable columns={columns} />
  ) : (
    // ####### https://github.com/techorin/orderX-backend/issues/122
    <StyledGridContainer
      headerHeight="201px"
      pagination={
        totalItems > 0 ? (
          <PaginationControl
            totalItems={totalItems}
            page={page}
            setPage={setPage}
            pageSize={limit}
            setPageSize={setLimit}
            loading={loading}
          />
        ) : undefined
      }
    >
      <StyledDataGrid
        rows={rows}
        columns={columns}
        disableColumnMenu
        sortModel={sortModel}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
        onSortModelChange={handleSortModelChange}
        sortingMode="server"
        sortingOrder={['asc', 'desc']}
      />
    </StyledGridContainer>
  );
};

export default GRNGrid;
